import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type GridColumn,
} from '@/components/desktop';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  SearchBar,
  errorMessage,
  shortDate,
} from '@/components/ui';
import { toast } from '@/stores/toast';
import { describeCode, useRegistry } from './PermissionGrid';

type Person = components['schemas']['MyPersonDto'];
type Limits = components['schemas']['MyLimitsDto'];
type HeldRole = components['schemas']['HeldRoleDto'];

/**
 * My People (.docs/027): the screen of someone the Managing Director has made
 * an access helper.
 *
 * It shows only what the delegation covers — these people, these roles, these
 * single permissions — so the limits are visible rather than discovered by
 * being refused. The server checks every one of them again, reading the
 * delegation fresh, so a role the MD has since widened stops being giveable
 * here at once.
 */
export function MyPeople() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<'roles' | 'permissions' | null>(null);

  const limitsQuery = useQuery({
    queryKey: ['my-people', 'limits'],
    queryFn: () => api.get('/api/v1/my-people/limits'),
  });
  const peopleQuery = useQuery({
    queryKey: ['my-people'],
    queryFn: () => api.get('/api/v1/my-people'),
  });

  const answered = limitsQuery.data as Limits | undefined;
  // The route answers for anyone, so "not a helper" is a plain answer rather
  // than a refusal: `isHelper` false means the rest is empty.
  const limits = answered?.isHelper ? answered : undefined;
  const people = useMemo(() => {
    const all = (peopleQuery.data ?? []) as Person[];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (p) => p.fullName.toLowerCase().includes(needle) || p.handle.toLowerCase().includes(needle),
    );
  }, [peopleQuery.data, search]);

  const selected = people.find((p) => p.id === selectedKey);

  if (limitsQuery.isSuccess && !limits) {
    return (
      <div className="workbench">
        <p className="muted">
          You are not an access helper at the moment. The Managing Director appoints them, and can
          end it at any time.
        </p>
      </div>
    );
  }

  const columns: Array<GridColumn<Person>> = [
    { key: 'name', header: 'Person', width: 200, render: (p) => p.fullName },
    { key: 'handle', header: 'User ID', width: 140, render: (p) => <span className="mono">{p.handle}</span> },
    {
      key: 'farms',
      header: 'Farms',
      width: 200,
      render: (p) => (
        <span className="dim">
          {p.allFarms ? 'All farms' : (p.farms as HeldRole[]).map((f) => f.code).join(', ') || '—'}
        </span>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      width: 200,
      render: (p) => (p.roles as HeldRole[]).map((r) => r.nameEn).join(', ') || <span className="dim">None</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      render: (p) =>
        p.status === 'ACTIVE' ? <span className="pill">Active</span> : <span className="pill pill--warn">Switched off</span>,
    },
  ];

  const toolbarActions: DesktopAction[] = selected
    ? [
        { label: 'Change roles', icon: 'edit', onSelect: () => setEditing('roles') },
        {
          label: 'Give or take away a permission',
          icon: 'checklist',
          onSelect: () => setEditing('permissions'),
        },
      ]
    : [];

  if (editing && selected && limits) {
    return (
      <PersonForm
        person={selected}
        limits={limits}
        what={editing}
        onCancel={() => setEditing(null)}
        onSaved={(message) => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['my-people'] });
          toast.success(message);
        }}
      />
    );
  }

  return (
    <div className="workbench">
      <SplitView
        id="iam-mypeople"
        direction="column"
        initial={340}
        firstLabel="people list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <SearchBar value={search} onChange={setSearch} placeholder="Search by name or user ID…" />
            </div>

            <ActionBar actions={toolbarActions} />

            {limits?.expiresAt && (
              <p className="settings__hint">
                What you may do here ends on {shortDate(limits.expiresAt)}.
              </p>
            )}
            {peopleQuery.isError && (
              <ErrorNote message={errorMessage(peopleQuery.error, 'Could not load your people')} />
            )}

            <DataGrid
              id="iam-mypeople"
              label="My people"
              columns={columns}
              rows={people}
              rowKey={(p) => p.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onOpen={(p) => {
                setSelectedKey(p.id);
                setEditing('roles');
              }}
              loading={peopleQuery.isPending}
              empty={
                search.trim()
                  ? 'Nobody matches your search.'
                  : 'Nobody yet. The Managing Director chooses who you look after.'
              }
            />
          </div>
        }
        second={
          <DetailPane
            tabs={
              limits
                ? [
                    {
                      id: 'limits',
                      label: 'What I may do',
                      icon: 'checklist',
                      content: <WhatIMayDo limits={limits} />,
                    },
                  ]
                : []
            }
            activeTab="limits"
            onTabChange={() => {}}
            empty="The Managing Director sets what you may do."
            title={selected && <span>{selected.fullName}</span>}
          />
        }
      />
      <StatusBar>
        <span>
          {people.length} {people.length === 1 ? 'person' : 'people'}
        </span>
        <span>{selected ? `Chosen: ${selected.fullName}` : 'Nobody chosen'}</span>
      </StatusBar>
    </div>
  );
}

/** The limits themselves, in the helper's own words. */
function WhatIMayDo({ limits }: { limits: Limits }) {
  const registry = useRegistry();
  return (
    <div className="stack">
      <section>
        <h3 className="field__label">Roles I may give or take away</h3>
        <p className="settings__choices">
          {limits.roles.length === 0 ? (
            <span className="dim">None</span>
          ) : (
            limits.roles.map((r) => (
              <span key={r.id} className={`pill${r.undelegable.length ? ' pill--warn' : ''}`}>
                {r.nameEn}
                {r.undelegable.length > 0 && ' — on hold'}
              </span>
            ))
          )}
        </p>
      </section>

      <section>
        <h3 className="field__label">Permissions I may give or take away</h3>
        <ul className="plainlist">
          {limits.permissions.length === 0 ? (
            <li className="dim">None</li>
          ) : (
            limits.permissions.map((code) => (
              <li key={code}>{describeCode(registry.data ?? [], code).text}</li>
            ))
          )}
        </ul>
      </section>

      <section>
        <h3 className="field__label">Adding people</h3>
        <p>
          {limits.canAddUsers
            ? 'You may add a sign-in account on the farms you look after, with the roles above.'
            : 'Only the Managing Director adds people.'}
        </p>
      </section>
    </div>
  );
}

/** Changing one person's roles, or one of their single permissions. */
function PersonForm({
  person,
  limits,
  what,
  onCancel,
  onSaved,
}: {
  person: Person;
  limits: Limits;
  what: 'roles' | 'permissions';
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const registry = useRegistry();
  const held = person.roles as HeldRole[];
  const [roleCodes, setRoleCodes] = useState<string[]>(held.map((r) => r.code));
  const [changes, setChanges] = useState<Record<string, 'grant' | 'revoke' | 'inherit'>>({});
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  // A role the Managing Director has since widened cannot be handed out, and
  // says so rather than failing on save.
  const giveable = limits.roles.filter((r) => r.undelegable.length === 0 && r.isActive);

  const save = useMutation({
    mutationFn: async () => {
      if (what === 'roles') {
        await api.patch(
          `/api/v1/my-people/${person.id}/roles` as '/api/v1/my-people/{id}/roles',
          { roleCodes, version: person.accessVersion },
        );
        return `${person.fullName}’s roles have been changed.`;
      }
      await api.patch(
        `/api/v1/my-people/${person.id}/permissions` as '/api/v1/my-people/{id}/permissions',
        {
          changes: Object.entries(changes).map(([code, state]) => ({ code, state })),
          reason: reason.trim(),
          version: person.accessVersion,
        },
      );
      return `${person.fullName}’s permissions have been changed.`;
    },
    onSuccess: onSaved,
  });

  function submit() {
    const issue =
      what === 'roles'
        ? roleCodes.length === 0
          ? 'Choose at least one role, or ask the Managing Director to switch this person off.'
          : null
        : Object.keys(changes).length === 0
          ? 'Nothing is changed yet.'
          : !reason.trim()
            ? 'Say why. It stays with the record.'
            : null;
    setProblem(issue);
    if (!issue) save.mutate();
  }

  return (
    <FormPanel
      title={what === 'roles' ? `${person.fullName}’s roles` : `${person.fullName}’s permissions`}
      intro="Only what the Managing Director put in your hands is shown here. Everything else about this person stays with them."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {what === 'roles' ? (
          <FormGroup title="Roles" card>
            <div className="settings__choices">
              {giveable.map((r) => {
                const on = roleCodes.includes(r.code);
                const code = r.code;
                return (
                  <button
                    key={r.id}
                    type="button"
                    className={`chip ${on ? 'chip--on' : ''}`}
                    aria-pressed={on}
                    onClick={() =>
                      setRoleCodes((list) =>
                        list.includes(code) ? list.filter((c) => c !== code) : [...list, code],
                      )
                    }
                  >
                    {on ? '✓ ' : ''}
                    {r.nameEn}
                  </button>
                );
              })}
            </div>
            {held.some((r) => !giveable.some((g) => g.code === r.code)) && (
              <p className="settings__hint">
                This person also holds a role the Managing Director has not put in your hands, so
                their roles cannot be changed here.
              </p>
            )}
          </FormGroup>
        ) : (
          <FormGroup title="Single permissions" card>
            <ul className="plainlist">
              {limits.permissions.map((code) => {
                const state = changes[code] ?? 'inherit';
                return (
                  <li key={code} className="settings__row">
                    <span>{describeCode(registry.data ?? [], code).text}</span>
                    <span className="settings__choices">
                      {(['grant', 'inherit', 'revoke'] as const).map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={`chip ${state === s ? 'chip--on' : ''}`}
                          aria-pressed={state === s}
                          onClick={() => setChanges((c) => ({ ...c, [code]: s }))}
                        >
                          {s === 'grant' ? 'Give' : s === 'revoke' ? 'Take away' : 'Leave to role'}
                        </button>
                      ))}
                    </span>
                  </li>
                );
              })}
            </ul>
            <Field label="Why" required value={reason} onChange={setReason} />
          </FormGroup>
        )}

        {problem && <ErrorNote message={problem} />}
        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            Save
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
